import { Box, Typography } from '@mui/material';
import { FC, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomTooltip } from 'src/components/tooltip';
import { ControlledField, FieldTypes } from 'src/components/controlled-field';
import { TextFieldTypes } from 'src/components/controlled-field/types';
import { sortWithPriority } from 'src/utils/sort-labels';

import { AuthContext } from '../../providers/auth-context';

import {
    countries,
    defaultOption,
    languages,
    nationalities,
    universitiesCountryMap,
    userDataKeys,
    studyYears,
} from './constants';
import { AddressDetailsProps, UserDataFormValues } from './types';
import { styles } from './styles';

export const AddressDetails: FC<AddressDetailsProps<UserDataFormValues>> = ({ control }) => {
    const { t } = useTranslation();
    const userState = useContext(AuthContext);

    const universities = [
        defaultOption,
        ...universitiesCountryMap.en,
        ...universitiesCountryMap.es,
    ];

    const sortedNationalities = useMemo(
        () => [
            defaultOption,
            ...sortWithPriority(
                t,
                nationalities,
                ['nationalities_british'],
                ['nationalities_other']
            ),
        ],
        [t]
    );

    const sortedCountries = useMemo(
        () => [
            defaultOption,
            ...sortWithPriority(t, countries, ['countries_united_kingdom'], ['countries_other']),
        ],
        [t]
    );

    let shouldDisableAddressFields = false;

    if (userState.loggedIn) {
        const { user_metadata: userDetails } = userState.session.user;

        shouldDisableAddressFields = Boolean(
            userDetails.address1 ||
                userDetails.address2 ||
                userDetails.city ||
                userDetails.postCode ||
                userDetails.country
        );
    }

    return (
        <Box css={styles.addressDetailsContainer}>
            <Box css={styles.addressSubtitle}>
                <Typography variant="h4">
                    {t('your_details_form_permanent_address_subtitle')}
                </Typography>
                <CustomTooltip
                    title={
                        <Typography variant="body4">
                            {t('your_details_form_permanent_address_info')}
                        </Typography>
                    }
                />
            </Box>

            <ControlledField
                name={userDataKeys.address1}
                control={control}
                label={t('your_details_form_address_1_label')}
                placeholder={t('your_details_form_address_1_placeholder')}
                fullWidth
                disabled={shouldDisableAddressFields}
                required
            />
            <ControlledField
                name={userDataKeys.address2}
                control={control}
                label={t('your_details_form_address_2_label')}
                placeholder={t('your_details_form_address_2_placeholder')}
                fullWidth
                disabled={shouldDisableAddressFields}
                required
            />
            <ControlledField
                name={userDataKeys.city}
                control={control}
                label={t('your_details_form_city_label')}
                placeholder={t('your_details_form_city_placeholder')}
                fullWidth
                disabled={shouldDisableAddressFields}
                required
            />

            <Box css={styles.inputGroupWrapper}>
                <ControlledField
                    name={userDataKeys.postCode}
                    control={control}
                    type={TextFieldTypes.POSTCODE}
                    label={t('your_details_form_post_code_label')}
                    placeholder={t('your_details_form_post_code_placeholder')}
                    fullWidth
                    disabled={shouldDisableAddressFields}
                    required
                />
                <ControlledField
                    fieldType={FieldTypes.Autocomplete}
                    name={userDataKeys.country}
                    control={control}
                    label={t('your_details_form_country_label')}
                    placeholder={t('your_details_form_country_placeholder')}
                    fullWidth
                    disabled={shouldDisableAddressFields}
                    required
                    options={sortedCountries}
                    id="country-input"
                />
            </Box>
            <Box css={styles.inputGroupWrapper}>
                <ControlledField
                    fieldType={FieldTypes.Autocomplete}
                    name={userDataKeys.nationality}
                    control={control}
                    options={sortedNationalities}
                    label={t('your_details_form_nationality_label')}
                    id="nationality-input"
                />
                <ControlledField
                    fieldType={FieldTypes.Autocomplete}
                    name={userDataKeys.preferredLanguage}
                    control={control}
                    options={languages}
                    label={t('your_details_form_language_label')}
                    id="preferred-lang-input"
                />
            </Box>
            <Box css={styles.inputGroupWrapper}>
                <ControlledField
                    fieldType={FieldTypes.Autocomplete}
                    name={userDataKeys.university}
                    control={control}
                    options={universities}
                    label={t('your_details_form_university_label')}
                    id="university-input"
                />
                <ControlledField
                    fieldType={FieldTypes.Autocomplete}
                    name={userDataKeys.studyYear}
                    control={control}
                    options={studyYears}
                    label={t('your_details_form_year_of_study_label')}
                    id="study-year-input"
                />
            </Box>
        </Box>
    );
};
